import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mail } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/customer/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="forgot-success" className="max-w-md mx-auto px-4 py-24 text-center">
        <Mail className="w-16 h-16 text-yellow-400 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-3xl font-black">Check your email.</h1>
        <p className="mt-4 text-slate-400 leading-relaxed">
          If an account exists for{" "}
          <span className="text-white font-mono text-sm">{email}</span>, we&apos;ve
          sent a password reset link. It&apos;s valid for 60 minutes.
        </p>
        <p className="mt-4 text-xs text-slate-500">
          Didn&apos;t get it? Check your spam folder — the email comes from
          onboarding@resend.dev.
        </p>
        <Link to="/signin" className="mt-8 inline-block text-yellow-400 hover:text-yellow-300 font-mono text-sm uppercase tracking-wider">
          ← Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="forgot-page" className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-400 text-slate-950 mb-4">
          <Mail className="w-6 h-6" />
        </div>
        <h1 className="font-[Outfit] text-3xl font-black tracking-tight">Forgot password?</h1>
        <p className="text-sm text-slate-400 mt-1">No worries — we&apos;ll email you a reset link.</p>

        <label className="block mt-8">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">Email</span>
          <input
            data-testid="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-yellow-400"
          />
        </label>

        {error && (
          <div data-testid="forgot-error" className="mt-4 text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded text-sm">{error}</div>
        )}

        <button
          data-testid="forgot-submit"
          type="submit"
          disabled={submitting}
          className="mt-6 w-full bg-yellow-400 text-slate-950 font-bold px-4 py-3 rounded-md uppercase tracking-wide hover:bg-yellow-300 disabled:opacity-50"
        >
          {submitting ? "Sending…" : "Send reset link"}
        </button>

        <div className="mt-4 text-xs text-center text-slate-500">
          Remembered it?{" "}
          <Link to="/signin" className="text-yellow-400 hover:text-yellow-300">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
